import {
  DailySummaryView,
  NotificationSettingsView,
  SummaryPreview,
  TelegramChannelView,
  EmailChannelView,
  NotificationChannels,
  NotificationMessage,
  NotificationRule,
  NotificationTestResult,
  type NotificationRuleInput,
  type NotificationSettingsInput,
  type UpdateEmailChannelRequest,
} from '@bartizo/shared';
import { z } from 'zod';
import { ApiError, apiRequest } from './client.js';

/** Notifications (knowledge doc sections 15 and 16; P9.1). */

export const getNotificationChannels = (): Promise<NotificationChannels> =>
  apiRequest('/notifications/channels', { schema: NotificationChannels });

export const saveEmailChannel = (body: UpdateEmailChannelRequest): Promise<EmailChannelView> =>
  apiRequest('/notifications/channels/email', { method: 'PUT', body, schema: EmailChannelView });

export const testEmailChannel = (to: string): Promise<NotificationTestResult> =>
  apiRequest('/notifications/channels/email/test', {
    method: 'POST',
    body: { to },
    schema: NotificationTestResult,
  });

export const listNotificationRules = (): Promise<NotificationRule[]> =>
  apiRequest('/notifications/rules', { schema: z.array(NotificationRule) });

export const createNotificationRule = (body: NotificationRuleInput): Promise<NotificationRule> =>
  apiRequest('/notifications/rules', { method: 'POST', body, schema: NotificationRule });

export const updateNotificationRule = (
  id: string,
  body: NotificationRuleInput,
): Promise<NotificationRule> =>
  apiRequest(`/notifications/rules/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    schema: NotificationRule,
  });

export const deleteNotificationRule = (id: string): Promise<NotificationRule> =>
  apiRequest(`/notifications/rules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: NotificationRule,
  });

export const testNotificationRule = (id: string): Promise<NotificationTestResult> =>
  apiRequest(`/notifications/rules/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    schema: NotificationTestResult,
  });

export const listNotificationMessages = (): Promise<NotificationMessage[]> =>
  apiRequest('/notifications/messages', { schema: z.array(NotificationMessage) });

export const saveTelegramChannel = (botToken: string): Promise<TelegramChannelView> =>
  apiRequest('/notifications/channels/telegram', {
    method: 'PUT',
    body: { botToken },
    schema: TelegramChannelView,
  });

export const deleteTelegramChannel = (): Promise<{ removed: boolean }> =>
  apiRequest('/notifications/channels/telegram', {
    method: 'DELETE',
    schema: z.object({ removed: z.boolean() }),
  });

export const testTelegramChannel = (chatId: string): Promise<NotificationTestResult> =>
  apiRequest('/notifications/channels/telegram/test', {
    method: 'POST',
    body: { chatId },
    schema: NotificationTestResult,
  });

export const getNotificationSettings = (): Promise<NotificationSettingsView> =>
  apiRequest('/notifications/settings', { schema: NotificationSettingsView });

export const saveNotificationSettings = (
  body: NotificationSettingsInput,
): Promise<NotificationSettingsView> =>
  apiRequest('/notifications/settings', { method: 'PUT', body, schema: NotificationSettingsView });

/** The latest daily summary, or `null` before the first. */
export async function getLatestSummary(): Promise<DailySummaryView | null> {
  try {
    return await apiRequest('/notifications/summary/latest', { schema: DailySummaryView });
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 404) return null;
    throw caught;
  }
}

export const previewSummary = (): Promise<SummaryPreview> =>
  apiRequest('/notifications/summary/preview', { schema: SummaryPreview });
